import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import {
  getOnboardingSession,
  updateOnboardingStep,
  completeOnboarding,
  uploadGalleryImage,
} from '../../api/onboarding';
import type {
  OnboardingSessionResponse,
  OnboardingStepData,
} from '../../types/onboarding';
import { STEPS } from '../../types/onboarding';
import { StepIndicator } from '../../components/onboarding/StepIndicator';
import { Step1ThemeAndTone } from '../../components/onboarding/steps/Step1ThemeAndTone';
import { Step3BrandAssets } from '../../components/onboarding/steps/Step3BrandAssets';
import { Step4BusinessDetails } from '../../components/onboarding/steps/Step4BusinessDetails';
import { Step5Location } from '../../components/onboarding/steps/Step5Location';
import { Step6Social } from '../../components/onboarding/steps/Step6Social';
import { Step7Industry } from '../../components/onboarding/steps/Step7Industry';
import { Step8YourStory } from '../../components/onboarding/steps/Step8YourStory';
import { Step8Team } from '../../components/onboarding/steps/Step8Team';
import { Step9Gallery } from '../../components/onboarding/steps/Step9Gallery';
import { Step10Review } from '../../components/onboarding/steps/Step10Review';
import { Step11AIProfile } from '../../components/onboarding/steps/Step11AIProfile';

export function OnboardingWizardPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<OnboardingSessionResponse | null>(
    null
  );
  const [currentStep, setCurrentStep] = useState(1);
  // Track the highest step the user has visited (for enabling forward navigation)
  const [highestStepVisited, setHighestStepVisited] = useState(1);
  const [stepData, setStepData] = useState<OnboardingStepData>({
    step1: null,
    step2: null,
    step3: null,
    step4: null,
    step5: null,
    step6: null,
    step7: null,
    step8: null,
    step9: null,
    stepTeam: null,
  });
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Debounce refs for API calls
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSaveRef = useRef<{ step: number | 'team'; data: object } | null>(null);
  // Track if initial load is complete to prevent saves during initialization
  const isInitializedRef = useRef(false);
  // Track current UI step in a ref for use in callbacks (avoids stale closure)
  const currentStepRef = useRef(currentStep);
  currentStepRef.current = currentStep;

  // Fetch session on load
  useEffect(() => {
    if (!sessionId) {
      setError('Invalid session');
      setLoading(false);
      return;
    }

    const fetchSession = async () => {
      try {
        const data = await getOnboardingSession(sessionId);
        setSession(data);
        setCurrentStep(data.session.currentStep);
        setHighestStepVisited(data.session.currentStep);
        setStepData(data.stepData);
        setLoading(false);
        // Mark as initialized after a short delay to allow child components to render
        setTimeout(() => {
          isInitializedRef.current = true;
        }, 100);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load session');
        setLoading(false);
      }
    };

    fetchSession();
  }, [sessionId]);

  // Actually perform the API save
  // Note: step can be a number (1-9) or 'team' for team data
  const performSave = useCallback(
    async (step: number | 'team', data: object) => {
      if (!sessionId) return;

      setSaving(true);
      try {
        // Pass the current UI step so the backend can track progress correctly
        await updateOnboardingStep(sessionId, step, data, currentStepRef.current);
      } catch (err) {
        console.error('Failed to save step:', err);
      } finally {
        setSaving(false);
      }
    },
    [sessionId]
  );

  // Debounced save - waits 500ms after last change before saving
  const debouncedSave = useCallback(
    (step: number | 'team', data: object) => {
      // Don't save during initial load
      if (!isInitializedRef.current) {
        return;
      }

      // Store the pending save data
      pendingSaveRef.current = { step, data };

      // Clear any existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Set a new timeout
      saveTimeoutRef.current = setTimeout(() => {
        const pending = pendingSaveRef.current;
        if (pending) {
          performSave(pending.step, pending.data);
          pendingSaveRef.current = null;
        }
      }, 500);
    },
    [performSave]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Update step data locally and trigger debounced auto-save
  // Note: Team data uses 'stepTeam' key and 'team' as the step identifier for API
  const handleStepDataChange = useCallback(
    (step: number | 'team', data: object) => {
      const key = step === 'team' ? 'stepTeam' : `step${step}` as keyof OnboardingStepData;
      setStepData((prev) => ({
        ...prev,
        [key]: data,
      }));
      debouncedSave(step === 'team' ? 'team' : step, data);
    },
    [debouncedSave]
  );

  // Scroll to top of page smoothly after React renders
  const scrollToTop = () => {
    // Use setTimeout to ensure scroll happens after React re-renders
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 50);
  };

  // Navigation (11 steps total)
  const goToStep = (step: number) => {
    if (step >= 1 && step <= 11) {
      setCurrentStep(step);
      scrollToTop();
    }
  };

  const goNext = () => {
    if (currentStep < 11) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      // Update highest step visited
      setHighestStepVisited(prev => Math.max(prev, nextStep));
      scrollToTop();
    }
  };

  const goBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      scrollToTop();
    }
  };

  // Handle gallery image upload (for Step 9)
  const handleGalleryImageUpload = async (file: File, slot: string): Promise<string> => {
    if (!sessionId) {
      throw new Error('No session ID');
    }
    const result = await uploadGalleryImage(sessionId, file, slot);
    return result.imageUrl;
  };

  // Submit onboarding
  const handleSubmit = async () => {
    if (!sessionId) return;

    setSubmitting(true);
    try {
      await completeOnboarding(sessionId);
      navigate('/onboarding-complete');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to complete onboarding'
      );
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-primary-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!session) {
    return null;
  }

  // Render current step
  const renderStep = () => {
    const commonProps = {
      onNext: goNext,
      onBack: goBack,
      saving,
    };

    // 11-step order:
    // 1. Industry → 2. Business → 3. Brand Assets → 4. Theme & Tone →
    // 5. Location → 6. Social → 7. Story → 8. Team → 9. Gallery → 10. Review → 11. AI Profile
    switch (currentStep) {
      case 1:
        // Industry first - determines everything else
        return (
          <Step7Industry
            {...commonProps}
            data={stepData.step7}
            onChange={(data) => handleStepDataChange(7, data)}
            isFirstStep={true}
            businessName={session.customer.businessName}
          />
        );
      case 2:
        // Business details - core identity + what makes you unique
        return (
          <Step4BusinessDetails
            {...commonProps}
            data={stepData.step4}
            onChange={(data) => handleStepDataChange(4, data)}
            businessName={session.customer.businessName}
          />
        );
      case 3:
        // Brand assets - logo + colors
        return (
          <Step3BrandAssets
            {...commonProps}
            data={stepData.step3}
            onChange={(data) => handleStepDataChange(3, data)}
            sessionId={sessionId!}
          />
        );
      case 4:
        // Theme & tone - now with colors available for preview
        return (
          <Step1ThemeAndTone
            {...commonProps}
            data={stepData.step1}
            onChange={(data) => handleStepDataChange(1, data)}
            businessName={session.customer.businessName}
          />
        );
      case 5:
        // Location & hours
        return (
          <Step5Location
            {...commonProps}
            data={stepData.step5}
            onChange={(data) => handleStepDataChange(5, data)}
          />
        );
      case 6:
        // Social & contact
        return (
          <Step6Social
            {...commonProps}
            data={stepData.step6}
            onChange={(data) => handleStepDataChange(6, data)}
            sessionId={sessionId!}
          />
        );
      case 7:
        // Your story - stats, credentials, testimonials
        return (
          <Step8YourStory
            {...commonProps}
            data={stepData.step8}
            onChange={(data) => handleStepDataChange(8, data)}
            yearsInBusinessFromStep4={stepData.step4?.years_in_business}
          />
        );
      case 8:
        // Meet the Team - owner/staff bios and photos
        return (
          <Step8Team
            {...commonProps}
            data={stepData.stepTeam}
            onChange={(data) => handleStepDataChange('team', data)}
            businessName={session.customer.businessName}
            onUploadImage={handleGalleryImageUpload}
          />
        );
      case 9:
        // Gallery & photos
        return (
          <Step9Gallery
            {...commonProps}
            data={stepData.step9}
            onChange={(data) => handleStepDataChange(9, data)}
            businessName={session.customer.businessName}
            industry={stepData.step7?.industry_category || session.customer.industry}
            onUploadImage={handleGalleryImageUpload}
          />
        );
      case 10:
        // Review - verify all information
        return (
          <Step10Review
            {...commonProps}
            stepData={stepData}
            customer={session.customer}
            onSubmit={goNext}
            submitting={false}
            onEditStep={goToStep}
          />
        );
      case 11:
        // AI Profile - brand summary
        return (
          <Step11AIProfile
            sessionId={sessionId!}
            businessName={session.customer.businessName}
            onSubmit={handleSubmit}
            onBack={goBack}
            saving={saving}
            submitting={submitting}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-xl font-bold text-primary-600">niftee</span>
            <span className="text-gray-300">|</span>
            <span className="text-gray-600 text-sm">
              {session.customer.businessName}
            </span>
          </div>
          {saving && (
            <span className="text-sm text-gray-500 flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" />
              Saving...
            </span>
          )}
        </div>
      </header>

      {/* Progress indicator */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <StepIndicator
          steps={STEPS}
          currentStep={currentStep}
          highestStepVisited={highestStepVisited}
          onStepClick={goToStep}
        />
      </div>

      {/* Step content */}
      <main className="max-w-2xl mx-auto px-4 pb-12">{renderStep()}</main>
    </div>
  );
}
